import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import BRAND from "../theme/talktokam";

const TermsOfService = () => {
  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 10, md: 14 },
          overflow: "hidden",
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "50%",
            right: "30%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`,
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
              }}
            >
              📄 Terms of Service
            </Typography>
            <Typography sx={{ fontSize: "1.1rem", color: "#94a3b8" }}>
              Last updated: June 2026
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Box
        sx={{
          background: "linear-gradient(180deg, #0a0e27 0%, #050816 100%)",
          py: { xs: 6, md: 10 },
        }}
      >
        <Container sx={{ maxWidth: 800 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Box
              sx={{
                "& h3": {
                  fontWeight: 700,
                  fontSize: "1.2rem",
                  color: "#fff",
                  mt: 4,
                  mb: 1.5,
                },
                "& p": {
                  fontSize: "0.95rem",
                  color: "#94a3b8",
                  lineHeight: 1.8,
                  mb: 2,
                },
                "& ul": { pl: 2.5, mb: 2 },
                "& li": { fontSize: "0.9rem", color: "#94a3b8", mb: 0.5 },
              }}
            >
              <h3>1. Acceptance of Terms</h3>
              <p>
                By accessing or using Talktokam ("the Service"), you agree to be
                bound by these Terms of Service. If you do not agree, you may
                not use the Service.
              </p>

              <h3>2. Description of Service</h3>
              <p>
                Talktokam provides an AI-powered chatbot platform that enables
                users to create, deploy, and manage conversational chatbots for
                their websites and applications.
              </p>

              <h3>3. Account Registration</h3>
              <p>
                You must create an account to use the Service. You are
                responsible for maintaining the confidentiality of your account
                credentials and for all activities that occur under your
                account. You must provide accurate and complete information
                during registration.
              </p>

              <h3>4. Acceptable Use</h3>
              <p>
                You agree not to use the Service for any unlawful purpose or in
                violation of any applicable laws. You may not use the Service to
                distribute malware, spam, or harmful content, or to impersonate
                others.
              </p>
              <p>
                Your chatbot conversations must comply with all applicable
                regulations, including data protection laws. You are responsible
                for the content and behavior of your chatbots.
              </p>

              <h3>4a. Instagram DM Compliance</h3>
              <p>
                If you connect an Instagram Business or Creator account to the
                Service, you agree to the following additional terms:
              </p>
              <ul>
                <li>You will not use automated messaging to send spam, unsolicited marketing, or abusive content via Instagram Direct Messages.</li>
                <li>Your automated responses must comply with Meta's Platform Terms, Instagram Community Guidelines, and Instagram Platform Policy.</li>
                <li>You are solely responsible for all messages sent from your connected Instagram account through our Service.</li>
                <li>You must maintain an accurate and honest Instagram Business profile that clearly represents your business.</li>
                <li>You must not use the Service to collect sensitive personal information (health data, financial information, government IDs) via Instagram DMs.</li>
                <li>You acknowledge that Meta may restrict or revoke API access if your account violates their terms.</li>
              </ul>

              <h3>5. Payment Terms</h3>
              <p>
                Paid plans are billed monthly or annually as selected during
                signup. Fees are non-refundable except as explicitly stated in
                our refund policy. We may change our fees with 30 days notice.
              </p>

              <h3>6. Intellectual Property</h3>
              <p>
                The Service, including its code, design, and branding, is owned
                by Talktokam, Inc. You retain ownership of the content you
                create using the Service, including chatbot configurations and
                conversation data.
              </p>

              <h3>7. Limitation of Liability</h3>
              <p>
                Talktokam shall not be liable for any indirect, incidental,
                special, consequential, or punitive damages arising out of or
                relating to your use of the Service. Our total liability is
                limited to the amount you paid for the Service in the 12 months
                preceding the claim.
              </p>

              <h3>8. Termination</h3>
              <p>
                We may terminate or suspend your account for violation of these
                terms. You may terminate your account at any time. Upon
                termination, your data will be deleted within 30 days unless
                required otherwise by law.
              </p>

              <h3>9. Changes to Terms</h3>
              <p>
                We reserve the right to modify these terms at any time. We will
                notify users of material changes via email or through the
                Service. Continued use after changes constitutes acceptance of
                the new terms.
              </p>

              <h3>10. Governing Law</h3>
              <p>
                These terms are governed by the laws of India. Any disputes
                shall be resolved in the courts of Mumbai, India.
              </p>

              <h3>11. Contact</h3>
              <p>
                For questions about these terms, contact us at
                contact@talktokam.com.
              </p>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default TermsOfService;
